// ============================================================
// dtbase v2：manifest（清单）Schema（对应《基座资产规范 v2》§3）
// ============================================================
import { z } from 'zod';
import { ASSET_TYPES, BASE_TIERS } from '../constants';
import { baseFlowTemplateSchema, baseModelRefSchema, basePointSchema } from '../base.schema';

const semverSchema = z.string().regex(/^\d+\.\d+\.\d+$/, '版本号需形如 1.0.0');

/** 权限块（后台可视化配置，服务端/MCP/前端统一校验） */
export const basePermissionSchema = z
  .object({
    tier: z.enum(BASE_TIERS).default('free'),
    access: z.array(z.enum(['included', 'addon', 'single', 'trial'])).min(1).default(['trial']),
    addonCode: z.string().max(64).nullish(),
    priceRef: z.string().max(128).nullish(),
    trialDays: z.number().int().min(0).max(365).default(0),
    allowExport: z.boolean().default(false),
  })
  .strict();
export type BasePermission = z.infer<typeof basePermissionSchema>;

const depsSchema = z
  .array(
    z
      .object({
        id: z.string().min(1).max(64),
        version: z.string().min(1).max(40),
        type: z.enum(ASSET_TYPES).optional(),
      })
      .strict(),
  )
  .max(50)
  .default([]);

/** I1：仿真参数（流体/能耗；供编辑器「仿真配置」与规则阈值联动） */
export const simulationParamSchema = z
  .object({
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(60),
    unit: z.string().max(20).default(''),
    default: z.number().optional(),
    warn: z.number().optional(),
    critical: z.number().optional(),
  })
  .strict();

/** I1：仿真参数包（按类别组织，可被基座 manifest 引用） */
export const simulationPackSchema = z
  .object({
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(60),
    category: z.enum(['fluid', 'energy']),
    description: z.string().max(300).optional(),
    params: z.array(simulationParamSchema).max(50).default([]),
  })
  .strict();

export type SimulationParam = z.infer<typeof simulationParamSchema>;
export type SimulationPack = z.infer<typeof simulationPackSchema>;

export const dtbaseManifestSchema = z
  .object({
    manifestVersion: z.literal('2.0'),
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z0-9][a-z0-9._-]*$/i, 'id 仅允许字母数字与 . _ -'),
    name: z.string().min(1).max(120),
    type: z.enum(ASSET_TYPES),
    industry: z.array(z.string().max(40)).max(10).default([]),
    version: semverSchema,
    schemaVersion: z.enum(['1.0', '2.0']).default('2.0'),
    minKernelVersion: semverSchema.optional(),
    description: z.string().max(1000).optional(),
    deps: depsSchema,
    permission: basePermissionSchema,
    /** 点位契约（供 AI 映射） */
    points: z.array(basePointSchema).max(200).default([]),
    /** 规则链模板 */
    flowTemplates: z.array(baseFlowTemplateSchema).max(50).default([]),
    /** 允许引用的模型/prefab（key + url 或 prefabId） */
    modelRefs: z.array(baseModelRefSchema.extend({ prefabId: z.string().max(128).optional() })).max(200).default([]),
    triBudget: z
      .object({ prefab: z.number().int().positive(), scene: z.number().int().positive() })
      .strict()
      .optional(),
    hardware: z.record(z.boolean()).optional(),
    lodSupport: z.boolean().default(false),
    publishProfile: z
      .array(z.enum(['web_url', 'exe_package', 'private_deploy', 'image_export']))
      .max(4)
      .default([]),
    preview: z
      .object({
        thumbnail: z.string().max(300).optional(),
        gallery: z.array(z.string().max(300)).max(20).optional(),
      })
      .strict()
      .optional(),
    tags: z.array(z.string().max(40)).max(20).default([]),
    i18n: z.record(z.object({ name: z.string().max(120) }).passthrough()).optional(),
    /** I1：仿真参数包（可选；编辑器读取后展示参数与阈值模板） */
    simulation: z.array(simulationPackSchema).max(10).optional(),
  })
  .strict();

export type DtBaseManifest = z.infer<typeof dtbaseManifestSchema>;
